/**
 * App entry (routing-and-state.md §6/§8). Wires the QueryClient (with the central 401
 * → signOut + bounce handler), the router (with the live auth context), and kicks off
 * `refresh()` on start to validate the session cookie. The `refresh()` promise is
 * recorded on the store so the `_authenticated` guard can await it (ENG-11) rather than
 * racing it.
 */
import { SolarProvider } from '@solar-icons/react/lib/SolarProvider';
import { RouterProvider } from '@tanstack/react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

// The shared design system (@argus/ui — the same files apps/portal loads).
import '@argus/ui/styles/globals.css';
import '@argus/ui/styles/primitives.css';
import '@argus/ui/styles/primitives-ext.css';
import '@argus/ui/styles/date-picker.css';

import { createAppQueryClient } from './lib/query-client.ts';
import { startSessionHeartbeat } from './lib/session-heartbeat.ts';
import { initTheme } from './lib/theme.ts';
import { createAppRouter } from './router/router.tsx';
import { installSessionGuard } from './router/session-guard.ts';
import { registerSessionReset, useAuth } from './store/auth-store.ts';

// Paint the stored theme preference before the first render (design-system.md §2.5).
initTheme();

// The 401 handler needs `navigate`; the router needs the QueryClient in its context.
// Break the cycle with a ref the handler reads once the router exists.
let router: ReturnType<typeof createAppRouter> | null = null;

const queryClient = createAppQueryClient({
  signOut: () => useAuth.getState().signOut(),
  dropFirm: () => useAuth.getState().dropFirm(),
  reselectFirm: () => useAuth.getState().reselectFirm(),
  resetQueries: () => queryClient.resetQueries(),
  currentHref: () => router?.latestLocation.href,
  navigate: (opts) => {
    void router?.navigate(opts);
  },
});

router = createAppRouter({ queryClient });
// Store change → navigation: a session the heartbeat found dead (nobody navigating, no query
// failing) lands on /login / re-selects the firm instead of leaving a dead shell on screen.
installSessionGuard(router);

// §6 sign-out AND every Switch firm clear the server cache (`queryClient.clear()`), behind the
// store's side-effect registry so the store never imports the QueryClient. The store awaits it,
// and itself owns the service-worker API cache purge (not wired here, so it cannot be dropped).
registerSessionReset(() => queryClient.clear());

// Validate the session cookie on start (the refresh whose in-flight promise the
// ENG-11 guard awaits).
void useAuth.getState().refresh();

// Keep the IDENTITY session alive while the tab is in use (it idles out after 30 min of
// no worker-auth traffic, and the app's steady-state traffic all goes to worker-api).
startSessionHeartbeat();

const rootEl = document.getElementById('root');
if (!rootEl) {
  throw new Error('Root element #root not found');
}

// Solar icon defaults (design-system.md icon ruling 2026-09-23: Line Duotone). The same
// values are declared on `:root` in globals.css (`--solar-*`) so portalled UI (Modal renders
// into document.body, outside this wrapper) gets the duotone second tone too.
createRoot(rootEl).render(
  <StrictMode>
    <SolarProvider
      size={16}
      strokeWidth={1.5}
      secondaryColor="var(--argus-icon-secondary)"
      secondaryOpacity={1}
    >
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </SolarProvider>
  </StrictMode>,
);
